import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from '../custom-snackbar/custom-snackbar.component';
import { NotificationSnackbarComponent } from './notification-snackbar.component';

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'default';

export interface NotificationOptions {
  duration?: number;
  horizontalPosition?: 'start' | 'center' | 'end' | 'left' | 'right';
  verticalPosition?: 'top' | 'bottom';
  panelClass?: string | string[];
  useNewComponent?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  constructor(private readonly snackbar: MatSnackBar) { }

  /**
   * Show notification using the legacy custom snackbar component
   * @param message - The message to display
   * @param action - The action type ('success', 'error', or empty string)
   */
  showNotification(message: string, action = '') {
    this.snackbar.openFromComponent(CustomSnackbarComponent, {
      data: { message, action },
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      duration: 2000,
      panelClass: action === 'error' ? 'error-message' : 'success-message'
    });
  }

  /**
   * Show notification using the new notification snackbar component
   * @param message - The message to display
   * @param type - The notification type
   * @param options - Additional options for the notification
   */
  showNotificationNew(
    message: string, 
    type: NotificationType = 'default', 
    options: NotificationOptions = {}
  ) {
    const defaultOptions = {
      duration: 4000,
      horizontalPosition: 'right' as const,
      verticalPosition: 'bottom' as const,
      panelClass: `notification-${type}`,
      useNewComponent: true
    };

    const finalOptions = { ...defaultOptions, ...options };

    this.snackbar.openFromComponent(NotificationSnackbarComponent, {
      data: { message, type },
      duration: finalOptions.duration,
      horizontalPosition: finalOptions.horizontalPosition,
      verticalPosition: finalOptions.verticalPosition,
      panelClass: finalOptions.panelClass
    });
  }

  /**
   * Show success notification
   * @param message - The success message
   * @param options - Additional options
   */
  showSuccess(message: string, options?: NotificationOptions) {
    this.showNotificationNew(message, 'success', options);
  }

  /**
   * Show error notification
   * @param message - The error message
   * @param options - Additional options
   */
  showError(message: string, options?: NotificationOptions) {
    this.showNotificationNew(message, 'error', options);
  }

  /**
   * Show warning notification
   * @param message - The warning message
   * @param options - Additional options
   */
  showWarning(message: string, options?: NotificationOptions) {
    this.showNotificationNew(message, 'warning', options);
  }

  /**
   * Show info notification
   * @param message - The info message
   * @param options - Additional options
   */
  showInfo(message: string, options?: NotificationOptions) {
    this.showNotificationNew(message, 'info', options);
  }

  /**
   * Show default notification
   * @param message - The message
   * @param options - Additional options
   */
  showDefault(message: string, options?: NotificationOptions) {
    this.showNotificationNew(message, 'default', options);
  }

  /**
   * Show persistent notification (no auto-dismiss)
   * @param message - The message
   * @param type - The notification type
   * @param options - Additional options
   */
  showPersistent(message: string, type: NotificationType = 'info', options?: NotificationOptions) {
    this.showNotificationNew(message, type, { ...options, duration: 0 });
  }

  /**
   * Show notification with custom duration
   * @param message - The message
   * @param type - The notification type
   * @param duration - Duration in milliseconds
   * @param options - Additional options
   */
  showWithDuration(
    message: string, 
    type: NotificationType = 'default', 
    duration: number, 
    options?: NotificationOptions
  ) {
    this.showNotificationNew(message, type, { ...options, duration });
  }

  /**
   * Dismiss all open notifications
   */
  dismissAll() {
    this.snackbar.dismiss();
  }
}
